import axios from "axios";
import toast from "react-hot-toast";
import useAppStore from "@components/Microsite/microsite-store";

/**
 * Ride Challenge user lookup utility
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.userInfo - User information (email, phone, etc.)
 * @param {Function} options.onModal - Modal callback for lock data
 * @returns {Promise} - Promise that resolves with user data
 */
export async function rideChallengeUserLookup({ userInfo = {}, onModal }) {
  // Get current store state and actions
  const {
    campaignId,
    location,
    coords,
    setUser,
    setEmail,
    setPhone,
    setEmailStreams,
    setRegistrationInfo,
    setActiveStep,
    setVerificationMethod,
    setPreferredLocation,
    entityInfo,
  } = useAppStore.getState();

  // Build request info
  const req = {
    location_id: location?.id,
  };

  if (entityInfo.campaign_effort_id)
    req.campaign_effort_id = entityInfo.campaign_effort_id;
  if (campaignId) req.campaign_id = campaignId;
  if (coords) {
    req.latitude = coords.latitude;
    req.longitude = coords.longitude;
  }

  // User info
  if (userInfo.email) req.email = userInfo.email;
  if (userInfo.mobile_phone) req.mobile_phone = userInfo.mobile_phone;

  try {
    const { data } = await axios.post(`/ride-challenge/setup/`, req);
    const user = data.response[0];

    if (user) {
      // Handle modal/lock data
      if (user.modal && onModal) {
        return onModal(user);
      }

      // Set user data in store
      setUser(user);
      setEmail(user.email || userInfo.email);
      setPreferredLocation(user.preferred_location || null);

      // Set email streams if available
      if (user.email_streams && user.email_streams.length) {
        setEmailStreams(
          user.email_streams.map((m) => ({
            ...m,
            value: m.active,
            label: m.name,
          }))
        );
      }

      // Set phone
      if (userInfo.mobile_phone) {
        setPhone(userInfo.mobile_phone);
      } else if (user.mobile_phone) {
        setPhone(user.mobile_phone);
      }

      // Ride challenge specific logic
      if (entityInfo.campaign_effort_id) {
        const foundEffort = user?.campaign_efforts?.find(
          (f) => f.id === entityInfo.campaign_effort_id
        );

        if (foundEffort) {
          setRegistrationInfo(foundEffort);

          if (!foundEffort.registered) {
            setActiveStep("registration");
          } else {
            setActiveStep("dashboard");
          }
        }
      }

      return user;
    } else {
      // No user found, set basic info
      if (userInfo.email) setEmail(userInfo.email);
      if (userInfo.mobile_phone) setPhone(userInfo.mobile_phone);
      return null;
    }
  } catch (error) {
    toast.error(error.message || "User lookup failed");
    throw error;
  }
}
